import React from 'react';
import styles from './ProgressBar.module.scss';

interface Step {
    number: number;
    label: string;
    subSteps?: {key: string}[];
}

interface ProgressBarProps {
    steps: Step[];
    currentStep: number;
    currentSubStepKey?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ steps, currentStep, currentSubStepKey }) => {
    const calculateConnectorFill = (step: Step) => {
        if (step.number === currentStep) {
            const subStepCount = step.subSteps?.length || 1;
            const currentSubIndex = step.subSteps?.findIndex(sub => sub.key === currentSubStepKey) ?? -1;
            return ((currentSubIndex + 1) / subStepCount) * 100;
        }
        return step.number < currentStep ? 100 : 0;
    };

    const currentStepData = steps.find(step => step.number === currentStep);
    const percentage = currentStepData ? calculateConnectorFill(currentStepData) : 0;

    return (
        <>
            <div className={styles.progressBar}>
                {steps.map((step, index) => {
                    const isActive = step.number <= currentStep;
                    const isCompleted = step.number < currentStep;
                    const isLast = index === steps.length - 1;
                    const connectorFill = calculateConnectorFill(step);
                    return (
                        <React.Fragment key={step.number}>
                            <div className={`${styles.step} ${isActive ? styles.active : ''} ${isCompleted ? styles.completed : ''}`}>
                                <div className={styles.stepNumber}>{step.number}</div>
                                <div className={`${styles.stepLabel} ${isActive || isCompleted ? styles.active : ''}`}>
                                    {step.label}
                                </div>
                            </div>
                            {!isLast && (
                                <div className={styles.connector}>
                                    <div
                                        className={styles.connectorFill}
                                        style={{ width: `${connectorFill}%` }}
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            <div className={styles.donutProgressBar}>
                {currentStepData && (
                    <div className={styles.donut}>
                        <svg viewBox="0 0 36 36" className={styles.circularChart}>
                            <path
                                className={styles.circleBg}
                                d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <path
                                className={styles.circle}
                                strokeDasharray={`${percentage}, 100`}
                                d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                        </svg>
                        <div className={styles.donutTextContainer}>
                            <div className={styles.donutStepNumber}>
                                {`${currentStepData.number}/6`}
                            </div>
                            <div className={styles.donutLabel}>
                                {currentStepData.label}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ProgressBar;
